import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { normalizeQuery } from "@/lib/normalizeQuery";

/**
 * Trending questions for the homepage.
 *
 * Primary source: the `trending_questions` table, populated by the
 * `refresh-trending-questions` edge function. That function clusters the
 * most recently published episodes (by published_at), blends in real user
 * search trends, applies admin-defined boost themes, and writes one
 * operator-style question per cluster. Admins can pin, edit, add manual
 * rows, and remove items via /admin?section=trending-topics.
 *
 * Fallback: if the table is empty (cold start / disabled), we fall back to
 * the legacy search_logs aggregation so the homepage never goes blank.
 *
 * Ordering: pinned first, then by score DESC, then display_order.
 */
export interface TrendingQuestion {
  text: string;
  count: number;
}

export function useTrendingQuestions(limit = 7) {
  const [questions, setQuestions] = useState<TrendingQuestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Primary: admin-managed table
        const { data: managed } = await supabase
          .from("trending_questions")
          .select("question_text, score, is_pinned, display_order")
          .eq("is_active", true)
          .order("is_pinned", { ascending: false })
          .order("score", { ascending: false })
          .order("display_order", { ascending: true })
          .limit(limit * 2); // pull extra to absorb client-side dedupe

        if (managed && managed.length > 0) {
          if (!cancelled) {
            // Belt-and-braces dedupe by normalized text, in case a stale
            // cache or future regression slips a duplicate through.
            const seen = new Set<string>();
            const unique: TrendingQuestion[] = [];
            for (const r of managed as any[]) {
              const key = String(r.question_text || "").toLowerCase().trim().replace(/\s+/g, " ");
              if (!key || seen.has(key)) continue;
              seen.add(key);
              unique.push({ text: r.question_text, count: 0 });
              if (unique.length >= limit) break;
            }
            setQuestions(unique);
          }
          return;
        }

        // Fallback: legacy search_logs aggregation
        const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const { data } = await supabase
          .from("search_logs")
          .select("query, normalized_query")
          .gte("created_at", since)
          .or("is_bot.is.null,is_bot.eq.false")
          .gt("results_count", 0)
          .limit(3000);

        const counts = new Map<string, { count: number; display: string }>();
        for (const row of data ?? []) {
          const q = (row.query || "").trim();
          if (!q || q.length < 6 || q.length > 140) continue;
          const key = normalizeQuery(row.normalized_query || q);
          if (!key) continue;
          const cur = counts.get(key);
          if (cur) cur.count += 1;
          else counts.set(key, { count: 1, display: q });
        }

        const top = Array.from(counts.values())
          .sort((a, b) => b.count - a.count)
          .slice(0, limit)
          .map((v) => ({ text: v.display, count: v.count }));

        if (!cancelled) setQuestions(top);
      } catch {
        if (!cancelled) setQuestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [limit]);

  return { questions, loading };
}
